import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import HeaderBar from 'components/ui/HeaderBar';
import RoleBasedSidebar from 'components/ui/RoleBasedSidebar';
import BreadcrumbNavigation from 'components/ui/BreadcrumbNavigation';
import Icon from 'components/AppIcon';
import BiodataDetailsView from 'components/BiodataDetailsView';
import { useAuth } from 'context/AuthContext';
import { useEmployerApplications } from 'hooks/useEmployerApplications';
import { employerAPI, biodataAPI } from 'utils/api';

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString();
};

const SubmittedApplicationsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { applications, loading: loadingApps, error: appsError } = useEmployerApplications();

  const [searchTerm, setSearchTerm] = useState('');
  const [onlyApproved, setOnlyApproved] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedVdrId, setSelectedVdrId] = useState(null);
  const [appsPanelCollapsed, setAppsPanelCollapsed] = useState(false);

  // Worker data state
  const [workersData, setWorkersData] = useState(null);
  const [loadingWorkers, setLoadingWorkers] = useState(false);
  const [workersError, setWorkersError] = useState(null);

  // Modal state for worker details
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeBiodata, setActiveBiodata] = useState(null);

  useEffect(() => {
    if (user && (!user.eclabUser || user.role !== 3)) {
      navigate('/login-screen');
    }
  }, [user, navigate]);

  // Load approved workers data
  const loadWorkersData = useCallback(async () => {
    try {
      setLoadingWorkers(true);
      setWorkersError(null);
      const response = await employerAPI.getApprovedWorkers({
        status: statusFilter,
        vdr_id: selectedVdrId
      });
      setWorkersData(response.data || response);
    } catch (err) {
      console.error('Failed to load workers data:', err);
      setWorkersError(err.response?.data?.error || err.message || 'Failed to load workers data');
    } finally {
      setLoadingWorkers(false);
    }
  }, [statusFilter, selectedVdrId]);

  useEffect(() => {
    loadWorkersData();
  }, [loadWorkersData]);

  const filteredApplications = useMemo(() => {
    const list = applications
      .filter(app => {
        const matchesSearch = searchTerm
          ? [app.vdrId, app.companyName, app.nationality, app.applicationId]
              .some(field => field?.toLowerCase().includes(searchTerm.toLowerCase()))
          : true;

        // If onlyApproved is on, make a best-effort pre-filter using local stats
        const looksApproved = (app.localStats?.workers?.approved ?? 0) > 0;
        return matchesSearch && (!onlyApproved || looksApproved);
      })
      .sort((a, b) => {
        return (b.slots?.approved ?? 0) - (a.slots?.approved ?? 0);
      });

    return list;
  }, [applications, searchTerm, onlyApproved]);

  // Handle viewing worker biodata details
  const handleViewWorker = useCallback(async (worker) => {
    setDetailModalOpen(true);
    setDetailLoading(true);
    setActiveBiodata(null);
    try {
      const result = await biodataAPI.getBiodataById(worker.biodata_id);
      if (result.status === 'success' && result.data) {
        setActiveBiodata(result.data);
      } else {
        setActiveBiodata({ error: result.message || 'Failed to load biodata details.' });
      }
    } catch (err) {
      console.error('Failed to fetch biodata details', err);
      setActiveBiodata({ error: err.response?.data?.error || err.message || 'Failed to load biodata details.' });
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const closeDetailModal = () => {
    setDetailModalOpen(false);
    setActiveBiodata(null);
    setDetailLoading(false);
  };

  const handleSidebarToggle = () => setSidebarCollapsed(prev => !prev);

  return (
    <div className="min-h-screen bg-gray-50">
      <HeaderBar
        userRole="employer"
        onSidebarToggle={handleSidebarToggle}
        userName={user?.fullName || 'Employer'}
      />

      <div className="flex pt-16">
        <RoleBasedSidebar
          isCollapsed={sidebarCollapsed}
          onToggle={handleSidebarToggle}
          activePath="/employer-submitted-applications"
        />

        <main className={`flex-1 p-6 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'}`}>
          <BreadcrumbNavigation
            items={[
              { label: 'Home', href: '/employer-dashboard' },
              { label: 'Submitted Applications' }
            ]}
          />

          <div className="mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">Submitted Applications</h1>
            <p className="text-sm text-gray-600 mt-1">View submitted applications and track associated workers.</p>
          </div>

          {/* Summary Statistics */}
          {workersData?.summary && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-medium">Total Workers</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">{workersData.summary.total || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <Icon name="Users" size={24} className="text-blue-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-medium">Approved</p>
                    <p className="text-2xl font-bold text-green-600 mt-1">{workersData.summary.approved || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <Icon name="CheckCircle" size={24} className="text-green-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-medium">Pending</p>
                    <p className="text-2xl font-bold text-amber-600 mt-1">{workersData.summary.pending || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                    <Icon name="Clock" size={24} className="text-amber-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-medium">Rejected</p>
                    <p className="text-2xl font-bold text-red-600 mt-1">{workersData.summary.rejected || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <Icon name="XCircle" size={24} className="text-red-600" />
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Applications list */}
            <section className={`bg-white rounded-lg shadow-sm border border-gray-200 transition-all duration-300 ${
              appsPanelCollapsed ? 'lg:col-span-1' : 'lg:col-span-4'
            }`}>
              <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                {!appsPanelCollapsed && <h2 className="font-semibold text-gray-900">Applications</h2>}
                <button
                  onClick={() => setAppsPanelCollapsed(!appsPanelCollapsed)}
                  className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                  title={appsPanelCollapsed ? 'Expand panel' : 'Collapse panel'}
                >
                  <Icon name={appsPanelCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={18} className="text-gray-600" />
                </button>
              </div>

              {!appsPanelCollapsed && (
                <>
                  <div className="p-4 border-b border-gray-100">
                <div className="space-y-3">
                  <div className="relative">
                    <Icon name="Search" size={16} className="absolute left-3 top-2.5 text-gray-400" />
                    <input
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search applications..."
                      className="w-full pl-9 pr-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" checked={onlyApproved} onChange={(e) => setOnlyApproved(e.target.checked)} />
                    Approved only
                  </label>
                </div>
              </div>

              <div className="max-h-[60vh] overflow-y-auto">
                {loadingApps && (
                  <div className="p-4 text-sm text-gray-500">Loading applications…</div>
                )}
                {appsError && (
                  <div className="p-4 text-sm text-red-600">{String(appsError)}</div>
                )}
                {!loadingApps && filteredApplications.length === 0 && (
                  <div className="p-4 text-sm text-gray-500">No applications found.</div>
                )}

                <ul className="divide-y divide-gray-100">
                  <li
                    className={`p-4 cursor-pointer hover:bg-gray-50 ${!selectedVdrId ? 'bg-blue-50 border-l-4 border-blue-500' : ''}`}
                    onClick={() => setSelectedVdrId(null)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-gray-900">All Applications</div>
                        <div className="text-xs text-gray-500 mt-1">View all approved workers</div>
                      </div>
                      <Icon name="List" size={18} className="text-gray-400" />
                    </div>
                  </li>
                  {filteredApplications.map(app => (
                    <li
                      key={app.vdrId}
                      className={`p-4 cursor-pointer hover:bg-gray-50 ${selectedVdrId === app.vdrId ? 'bg-blue-50 border-l-4 border-blue-500' : ''}`}
                      onClick={() => setSelectedVdrId(app.vdrId)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900">{app.vdrId}</div>
                          <div className="text-xs text-gray-500 mt-1">{app.vdrType || '—'} • {app.nationality || '—'}</div>
                          <div className="mt-2 flex flex-wrap gap-1">
                            <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[10px]">
                              Slots: {app.slots?.approved ?? 0}
                            </span>
                            <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-[10px]">
                              Workers: {app.localStats?.workers?.approved ?? 0}
                            </span>
                          </div>
                        </div>
                        <div className="text-right ml-2">
                          {app.category?.label && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-700">
                              {app.category.label}
                            </span>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
                </>
              )}
            </section>

            {/* Workers Table */}
            <section className={`bg-white rounded-lg shadow-sm border border-gray-200 transition-all duration-300 ${
              appsPanelCollapsed ? 'lg:col-span-11' : 'lg:col-span-8'
            }`}>
              <div className="p-4 border-b border-gray-100">
                <div className="mb-3">
                  <h2 className="font-semibold text-gray-900 mb-3">
                    {selectedVdrId ? `Workers - ${selectedVdrId}` : 'All Workers'}
                  </h2>
                </div>
                <div className="flex items-center gap-3">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="all">All Status</option>
                    <option value="approved">Approved</option>
                    <option value="pending">Pending</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              </div>

              <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
                {loadingWorkers && (
                  <div className="p-4 text-sm text-gray-500">Loading workers…</div>
                )}
                {workersError && (
                  <div className="p-4 text-sm text-red-600">{String(workersError)}</div>
                )}
                {!loadingWorkers && workersData?.workers?.length === 0 && (
                  <div className="p-4 text-sm text-gray-500">No workers found.</div>
                )}
                {!loadingWorkers && workersData?.workers && workersData.workers.length > 0 && (
                  <table className="w-full">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Worker</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">VDR</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timeline</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {workersData.workers.map((worker) => (
                        <tr key={worker.application_worker_id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                                {worker.photo_url ? (
                                  <img src={worker.photo_url} alt={worker.full_name} className="w-full h-full object-cover rounded-full" />
                                ) : (
                                  <Icon name="User" size={16} className="text-gray-400" />
                                )}
                              </div>
                              <div>
                                <div className="font-medium text-gray-900 text-sm">{worker.full_name}</div>
                                <div className="text-xs text-gray-500">{worker.passport_no}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm text-gray-900">{worker.vdr.id}</div>
                            <div className="text-xs text-gray-500">{worker.vdr.type}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
                              worker.status === 'approved' ? 'bg-green-100 text-green-700' :
                              worker.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                              worker.status === 'rejected' ? 'bg-red-100 text-red-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {worker.status}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-xs text-gray-500 space-y-1">
                              {worker.timeline.approved_at && (
                                <div className="flex items-center gap-1">
                                  <Icon name="CheckCircle" size={12} className="text-green-600" />
                                  <span>Approved: {formatDate(worker.timeline.approved_at)}</span>
                                </div>
                              )}
                              {worker.timeline.rejected_at && (
                                <div className="flex items-center gap-1">
                                  <Icon name="XCircle" size={12} className="text-red-600" />
                                  <span>Rejected: {formatDate(worker.timeline.rejected_at)}</span>
                                </div>
                              )}
                              {!worker.timeline.approved_at && !worker.timeline.rejected_at && (
                                <div>Submitted: {formatDate(worker.timeline.submitted_at)}</div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => handleViewWorker(worker)}
                              className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-xs font-medium flex items-center"
                            >
                              <Icon name="Eye" size={12} className="mr-1" />
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </section>
          </div>

          {/* Biodata detail modal */}
          {detailModalOpen && (
            <>
              <style>{`
                @media print {
                  .biodata-modal-overlay {
                    display: none !important;
                  }
                  .biodata-modal-content {
                    position: static !important;
                    max-width: 100% !important;
                    max-height: none !important;
                    box-shadow: none !important;
                  }
                }
              `}</style>
              <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 biodata-modal-overlay">
                <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] overflow-y-auto biodata-modal-content">
                  <div className="p-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
                    <h3 className="font-semibold text-gray-900">Worker Details</h3>
                    <button onClick={closeDetailModal} className="text-gray-500 hover:text-gray-700">
                      <Icon name="X" size={18} />
                    </button>
                  </div>
                  <div className="p-4">
                    {detailLoading && <div className="text-sm text-gray-500">Loading biodata…</div>}
                    {!detailLoading && activeBiodata && !activeBiodata.error && (
                      <BiodataDetailsView biodata={activeBiodata} />
                    )}
                    {!detailLoading && activeBiodata?.error && (
                      <div className="text-sm text-red-600">{String(activeBiodata.error)}</div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default SubmittedApplicationsPage;